import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { ToyCycleLogo } from "./Logo";
import { logError } from "../lib/api";

/**
 * Per-route error boundary. Catches render errors AND lazy-chunk load failures
 * so the user gets a friendly card instead of a blank page.
 *
 * Reset key: pass `routeKey={location.pathname}` and the boundary auto-resets
 * when the route changes (so navigating away "fixes" the broken state).
 */
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    logError("RouteErrorBoundary caught:", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.routeKey !== this.props.routeKey) {
      this.setState({ error: null });
    }
  }

  handleRetry = () => {
    // Bust any stale failed-chunk cache and reload current route.
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const isChunkError =
      /chunk|loading|Loading.+failed|ChunkLoadError/i.test(String(this.state.error));
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center p-6"
        data-testid="route-error-boundary"
        role="alert"
      >
        <div className="brutalist-card p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <ToyCycleLogo size={56} />
          </div>
          <div className="w-12 h-12 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-gray-900">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-black mb-2" style={{ fontFamily: "Outfit" }}>
            {isChunkError ? "Couldn't load this page" : "Something went wrong"}
          </h1>
          <p className="text-gray-600 mb-6 text-sm">
            {isChunkError
              ? "We had trouble loading this page (your connection may be flaky). Refresh and we'll try again."
              : "An unexpected error happened on this page. You can refresh or head back home."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleRetry}
              className="brutalist-btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="route-error-retry"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <Link
              to="/marketplace"
              onClick={() => this.setState({ error: null })}
              className="brutalist-btn-secondary flex-1 text-center"
              data-testid="route-error-home"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
